import { useEffect, type FC } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { LuX, LuMaximize2 } from 'react-icons/lu';
import DealDetailsPage from '../pages/DealDetailsPage';
import './OportunidadeDrawer.css';

/**
 * A oportunidade inteira num painel lateral, sem sair da tela que abriu.
 * Pedido dele em 2026-09-23, na tela de Tarefas: "abra um drawer da oportunidade
 * para o usuário verificar sem sair da tela de tarefas… ele pode ser mais largo
 * para aparecer praticamente toda a visão da oportunidade".
 *
 * Aqui não há cópia da tela do negócio: o painel renderiza a PRÓPRIA
 * `DealDetailsPage`, que já aceita o id por prop. Duplicar as seções dela
 * (negócio, contato, organização, próximo passo, produtos, anotações) seria
 * manter duas versões da mesma regra — e uma delas envelheceria calada.
 */

interface Props {
    conversaId: number | null;
    onFechar: () => void;
}

const OportunidadeDrawer: FC<Props> = ({ conversaId, onFechar }) => {
    const navigate = useNavigate();

    /* Esc fecha, e o corpo para de rolar atrás do painel. */
    useEffect(() => {
        if (!conversaId) return;
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
        document.addEventListener('keydown', aoTeclar);
        const rolagem = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', aoTeclar);
            document.body.style.overflow = rolagem;
        };
    }, [conversaId, onFechar]);

    if (!conversaId) return null;

    /* Portal: o painel nasce fora da tabela, senão qualquer `overflow` da lista
       o recortaria — a mesma razão do overlay da sidebar. */
    return createPortal(
        <div className="od-veu" onMouseDown={e => { if (e.target === e.currentTarget) onFechar(); }}>
            <aside className="od-painel" role="dialog" aria-label="Oportunidade">
                <div className="od-topo">
                    <span className="od-titulo">Oportunidade</span>
                    <span className="od-acoes">
                        <button type="button" className="od-btn" onClick={() => navigate(`/pipeline/deal/${conversaId}`)}>
                            <LuMaximize2 size={13} /> Abrir em tela cheia
                        </button>
                        <button type="button" className="od-btn is-icon" onClick={onFechar}
                            aria-label="Fechar" title="Fechar">
                            <LuX size={15} />
                        </button>
                    </span>
                </div>
                <div className="od-corpo">
                    <DealDetailsPage dealId={String(conversaId)} />
                </div>
            </aside>
        </div>,
        document.body,
    );
};

export default OportunidadeDrawer;
